import { AllAccessibleColumns, AttributeGroup } from '../../../shared/types';
import { BulkAttributeConfig } from '../../../shared/components/bulkUpdate';

const TARGET_GROUP_NAMES_EN = ['Manufacturing Engineering', 'Work preparator'] as const;

const BULK_UPDATE_INTERNAL_NAMES = [
  'aenderungen',
  'anz_ac',
  'anzustrebender_liefertermin',
  'arbeitspaket',
  'bauteilindex',
  'bemerkung',
  'beschafferschluessel',
  'betroffene_schale',
  'bottomup',
  'capex_id',
  'capex_stunden',
  'cmmhrs',
  'design_owner_id',
  'drawing_set_issue',
  'erledigt_me',
  'fpq',
  'hardtooling_euro',
  'htz_released_date',
  'kennwort',
  'make_or_buy_intern_qsfa_qsfb',
  'me_signed',
  'mod_fertigung',
  'mod_nr',
  'mp_nr',
  'pf_lc',
  'poe',
  'ps',
  'psp_c_a_element_cmm',
  'psp_c_a_element_femi',
  'psp_c_a_element_ncp',
  'speedline',
  'spezi_fertigung',
  'standard_leadtime',
  'steering_state',
  'suffix',
  'techno_code',
  'tot_stueckzahl',
  'twopagdatum',
  'twopager',
  'werk',
  'zustaendiger_k_berater',
  'apl_angelegt',
  'arbeitsplatz',
  'beauf_vollstaendig',
  'bemerkung_av',
  'erledigt_av',
  'fremdanf_datum',
  'fremdanfrage_gest',
  'hardtooling',
  'hardtooling_2',
  'hardtooling_3',
  'hardtooling_datum',
  'hardtooling_datum_2',
  'hardtooling_datum_3',
  'mat_ident_nr',
  'mat_stamm_angelegt',
  'ncb_gruen',
  'ncb_gruen_2',
  'ncb_gruen_3',
  'ncb_ident_nr',
  'ncb_ident_nr_2',
  'ncb_ident_nr_3',
  'pgz',
  'planer',
  'plangruppe',
  'plm_eingang',
  'termine_abgestimmt',
] as const;

export type PreparedBulkUpdateDialogData = {
  attributes: BulkAttributeConfig[];
  attributeGroups: AttributeGroup[];
};

function resolveGroupsForTargets(allAccessibleColumns: AllAccessibleColumns): AttributeGroup[] {
  const targetSet = new Set<string>(TARGET_GROUP_NAMES_EN);
  const foundGroups = new Map<string, AttributeGroup>();

  for (const column of allAccessibleColumns) {
    const group = column.mbdAttributeGroup;
    const enName = group?.attributeGroupNameen?.trim();

    if (!enName || !targetSet.has(enName) || foundGroups.has(enName)) {
      continue;
    }

    foundGroups.set(enName, {
      id: column.attrGroupId ?? 0,
      attributeGroupNameen: enName ?? '',
      attributeGroupNamede: group?.attributeGroupNamede?.trim() ?? enName,
    });

    if (foundGroups.size === targetSet.size) {
      break;
    }
  }

  return Array.from(foundGroups.values());
}

export function prepareBulkUpdateDialogData(
  allAccessibleColumns: AllAccessibleColumns | null | undefined,
): PreparedBulkUpdateDialogData {
  const includeInternalNameSet = new Set<string>(BULK_UPDATE_INTERNAL_NAMES as readonly string[]);

  if (!allAccessibleColumns?.length || includeInternalNameSet.size === 0) {
    return {
      attributes: [],
      attributeGroups: [],
    };
  }

  const attributeGroups = resolveGroupsForTargets(allAccessibleColumns);

  const allowedGroupIds = new Set(attributeGroups.map((group) => group.id));

  const attributes = allAccessibleColumns
    .filter((column) => {
      if (!includeInternalNameSet.has(column.attrInternalName)) return false;

      if (!allowedGroupIds.has(column.attrGroupId)) return false;

      return true;
    })
    .map((column) => ({
      id: column.id,
      attrGroupId: column.attrGroupId,
      attrInternalName: column.attrInternalName,
      attrDisplayNameen: column.attrDisplayNameen,
      attrDisplayNamede: column.attrDisplayNamede,
      attrDataType: column.attrDataType,
      attrMinValue: column.attrMinValue,
      attrMaxValue: column.attrMaxValue,
      formOrderNo: column.formOrderNo,
      valueOptions: column.valueOptions,
    }))
    .sort((a, b) => {
      const aOrder = a.formOrderNo ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.formOrderNo ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

  return {
    attributes,
    attributeGroups,
  };
}

export { TARGET_GROUP_NAMES_EN as BULK_UPDATE_TARGET_GROUP_NAMES_EN };
